import { z } from 'zod';

/** Contratos del reproductor integrado (D-26): lo que la interfaz necesita para enseñar
 *  una película ANTES de decidir qué hacer con ella —qué pistas trae, cuáles se pueden
 *  reproducir directas y cuáles habrá que recodificar al vuelo— y cómo pedir el vídeo.
 *
 *  El navegador no reproduce un MKV, ni HEVC, ni AC3/EAC3/DTS con garantías: lo que sí
 *  entiende en todas partes es H.264 de 8 bits con AAC dentro de un MP4 fragmentado. Así
 *  que el servidor sirve exactamente eso, copiando lo que ya vale y recodificando lo que
 *  no, y aquí se dice por adelantado qué va a pasar con cada pista. */

/** De qué fichero de la descarga se está hablando. Una misma película puede tener hasta
 *  tres: el original de aMule, el MP4 que espera en conversiones y la copia que ya está en
 *  la biblioteca. */
export const claseFuenteSchema = z.enum(['original', 'convertida', 'colocada']);
export type ClaseFuente = z.infer<typeof claseFuenteSchema>;

/** `directa` es «se copia tal cual»; `recodificar` es «ffmpeg lo convierte al vuelo». */
export const entregaSchema = z.enum(['directa', 'recodificar']);
export type Entrega = z.infer<typeof entregaSchema>;

/** Cómo llega un subtítulo al navegador. Uno de texto va como pista WebVTT aparte y se
 *  puede encender y apagar sin tocar el vídeo; uno de imagen (PGS, VobSub) no tiene forma
 *  de pintarse en un `<video>`, así que se INCRUSTA en la imagen, y eso obliga a
 *  recodificar el vídeo aunque fuera H.264. */
export const entregaSubtituloSchema = z.enum(['texto', 'incrustar']);
export type EntregaSubtitulo = z.infer<typeof entregaSubtituloSchema>;

export const videoReproduccionSchema = z.object({
  codec: z.string(),
  ancho: z.number().int().nullable(),
  alto: z.number().int().nullable(),
  hdr: z.enum(['hdr10', 'hlg', 'dolby_vision']).nullable(),
  entrega: entregaSchema,
  /** Por qué se copia o se recodifica, con palabras: «HEVC: el navegador no lo lee». */
  motivo: z.string(),
  /** La cadena `codecs` de MSE para ESTA entrega (`avc1.64001f`). Si se acaba
   *  recodificando por otro motivo —un subtítulo incrustado— vale `CODEC_MSE_H264_RECODIFICADO`. */
  codecMse: z.string(),
});
export type VideoReproduccion = z.infer<typeof videoReproduccionSchema>;

export const audioReproduccionSchema = z.object({
  /** Índice dentro de su tipo: el mismo `0:a:N` que usa el plan de pistas. */
  indice: z.number().int().min(0),
  codec: z.string(),
  idioma: z.string().nullable(),
  titulo: z.string().nullable(),
  canales: z.number().int().nullable(),
  porDefecto: z.boolean(),
  entrega: entregaSchema,
  motivo: z.string(),
  codecMse: z.string(),
});
export type AudioReproduccion = z.infer<typeof audioReproduccionSchema>;

export const subtituloReproduccionSchema = z.object({
  indice: z.number().int().min(0),
  codec: z.string(),
  idioma: z.string().nullable(),
  titulo: z.string().nullable(),
  porDefecto: z.boolean(),
  forzado: z.boolean(),
  entrega: entregaSubtituloSchema,
});
export type SubtituloReproduccion = z.infer<typeof subtituloReproduccionSchema>;

export const fuenteReproduccionSchema = z.object({
  clase: claseFuenteSchema,
  fileName: z.string(),
  ruta: z.string(),
  sizeBytes: z.number().int().nullable(),
  duracionSegundos: z.number().nullable(),
  video: videoReproduccionSchema.nullable(),
  audio: z.array(audioReproduccionSchema),
  subtitulos: z.array(subtituloReproduccionSchema),
});
export type FuenteReproduccion = z.infer<typeof fuenteReproduccionSchema>;

export const reproduccionDescargaSchema = z.object({
  downloadId: z.string(),
  titulo: z.string(),
  /** Vacío cuando no queda ningún fichero en disco: la pantalla lo dice en vez de fallar. */
  fuentes: z.array(fuenteReproduccionSchema),
});
export type ReproduccionDescarga = z.infer<typeof reproduccionDescargaSchema>;

/** Lo que se le pide al stream. Va en la query, así que todo llega como texto. */
export const peticionStreamSchema = z.object({
  fuente: claseFuenteSchema,
  audio: z.coerce.number().int().min(0),
  /** Índice del subtítulo a INCRUSTAR, o nada. Los de texto no pasan por aquí: van como
   *  pista WebVTT aparte. */
  subtitulo: z.coerce.number().int().min(0).optional(),
  /** Segundo desde el que empezar. El stream sale con los tiempos ABSOLUTOS del fichero,
   *  así que el navegador coloca lo que llega sin restar nada. */
  t: z.coerce.number().min(0).default(0),
});
export type PeticionStream = z.infer<typeof peticionStreamSchema>;

/** Una VENTANA de un subtítulo de texto: las entradas entre `desde` y `hasta`, en segundos
 *  del fichero. Sacar un subtítulo entero es leer el MKV de cabo a rabo —minutos con seis
 *  gigas en un disco ocupado— y no se pinta nada hasta el final; por ventanas, ffmpeg salta
 *  por el índice del contenedor y lee solo lo que hay entre las dos marcas, así que las
 *  primeras líneas llegan en segundos. Sin `hasta` se lee hasta el final. */
export const peticionSubtituloSchema = z.object({
  fuente: claseFuenteSchema,
  desde: z.coerce.number().min(0).default(0),
  hasta: z.coerce.number().min(0).optional(),
});
export type PeticionSubtitulo = z.infer<typeof peticionSubtituloSchema>;

/** Lo que produce libx264 cuando recodificamos: High a nivel 4.0. Se fija en el comando
 *  para que esta cadena sea cierta siempre, en vez de depender de lo que decida x264. */
export const CODEC_MSE_H264_RECODIFICADO = 'avc1.640028';

/** AAC-LC, que es lo único que se produce y lo único que se copia. */
export const CODEC_MSE_AAC = 'mp4a.40.2';

/** Altura del vídeo cuando hay que recodificarlo al vuelo. Es una vista previa, no la
 *  conversión: a 720p `libx264` va holgado en tiempo real en una CPU normal, y a 1080p no
 *  siempre. Lo que ya es H.264 se copia entero, sin reescalar. */
export const ALTURA_VISTA_PREVIA = 720;

export function mimeMse(codecVideo: string, codecAudio: string): string {
  return `video/mp4; codecs="${codecVideo},${codecAudio}"`;
}
